import type { Permission } from '@pms/shared';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import {
  BedDouble,
  BookOpen,
  Building2,
  Bus,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  Clock3,
  ClipboardCheck,
  ConciergeBell,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  MoonStar,
  TriangleAlert,
  Plus,
  Receipt,
  Search,
  ShieldCheck,
  Eye,
  Users,
  Coins,
  UserCog,
  Sparkles,
  BedSingle,
  PackageSearch,
  Wrench,
  UtensilsCrossed,
  Soup,
  Coffee,
  Sun,
  Waves,
  CookingPot,
  Calculator,
  HardHat,
  Shield,
  ChefHat,
  BarChart3,
  FileBarChart,
  FileText,
  PartyPopper,
  UsersRound,
  BookText,
  HandCoins,
  Truck,
  Landmark,
  Tags,
  Network,
  Globe,
  Plug,
  BellRing,
  Warehouse,
  Cog,
  FileLock2,
  Siren,
  CalendarClock,
  Target,
  ClipboardCheck as InspectIcon,
  Repeat2,
  CalendarHeart,
  IdCard,
  Shirt,
  Handshake,
  Wallet,
  Laptop,
  Briefcase,
  Megaphone,
  ListChecks,
  ListTodo,
  ShieldAlert,
} from 'lucide-react';
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cls, date } from '@/lib/format';
import { useLookups } from '@/lib/lookups';
import { useEmergencySync } from '@/lib/emergency';
import { useRoomReadyAlerts } from '@/lib/room-ready';
import { ShiftButton, ShiftGate } from './shift';
import { getDevice } from '@/lib/device';
import { ClockButton } from './clock';
import { Button, Spinner } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perm?: Permission;
  /** Shown when the user has any one of these. */
  any?: Permission[];
  /** Hidden from users who hold this (e.g. My Rooms is the attendants' page, not the managers'). */
  hide?: Permission;
  end?: boolean;
}
export const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: <LayoutDashboard />, perm: 'dashboard.view', end: true },
      { to: '/reports', label: 'Reports', icon: <FileText />, any: ['reports.frontoffice', 'reports.financial', 'reports.management', 'reports.payments', 'reports.nightaudit'] },
      // The Auditor's read-only audit trail sits with the reports (owner, 2026-10-26); the administrator's under Administration.
      { to: '/admin/audit', label: 'Audit Trail', icon: <ShieldCheck />, perm: 'audit.trail', hide: 'admin.audit_log' },
      { to: '/targets', label: 'Targets', icon: <Target />, perm: 'kpi.view' },
      { to: '/my-hr', label: 'My HR', icon: <CalendarHeart />, perm: 'leave.request' },
      { to: '/team-shifts', label: 'Shifts', icon: <Clock3 />, any: ['attendance.manage', 'hr.view'] },
    ],
  },
  {
    section: 'Front Office',
    items: [
      // The desk's, the GM's, the owner's and the Auditor's; Sales books nothing (owner, 2026-10-30).
      { to: '/front-desk', label: 'Front Desk', icon: <ConciergeBell />, any: ['frontdesk.checkin', 'reports.moneywatch', 'audit.signoff'] },
      // Porters (no Front Desk): the messages and parcels they take and hand over.
      { to: '/desk-messages', label: 'Messages & Parcels', icon: <Mail />, perm: 'frontdesk.messages', hide: 'reservations.view' },
      { to: '/service', label: 'Guest Service', icon: <BellRing />, any: ['service.view', 'service.work', 'service.log'] },
      { to: '/emergency', label: 'Emergency List', icon: <Siren />, perm: 'frontdesk.emergency_list' },
      { to: '/reservations', label: 'Reservations', icon: <BookOpen />, perm: 'reservations.view', end: true },
      { to: '/reservations/new', label: 'New Reservation', icon: <Plus />, perm: 'reservations.create' },
      { to: '/room-rack', label: 'Room Rack', icon: <BedDouble />, perm: 'rooms.view' },
      { to: '/tape-chart', label: 'Tape Chart', icon: <CalendarRange />, perm: 'reservations.view' },
      { to: '/availability', label: 'Availability', icon: <CalendarDays />, perm: 'reservations.view' },
    ],
  },
  {
    section: 'Rooms Division',
    items: [
      { to: '/housekeeping', label: 'Housekeeping', icon: <Sparkles />, perm: 'housekeeping.assign' },
      { to: '/laundry', label: 'Laundry', icon: <Shirt />, any: ['laundry.view', 'laundry.linen', 'laundry.guest'] },
      { to: '/my-rooms', label: 'My Rooms', icon: <BedSingle />, perm: 'housekeeping.update_status', hide: 'housekeeping.assign' },
      { to: '/my-rooms?tab=inspect', label: 'Inspections', icon: <InspectIcon />, perm: 'housekeeping.inspect' },
      { to: '/lost-found', label: 'Lost & Found', icon: <PackageSearch />, any: ['lostfound.manage', 'lostfound.log', 'lostfound.view'] },
      { to: '/maintenance', label: 'Maintenance', icon: <HardHat />, any: ['maintenance.view', 'engineering.work'] },
      { to: '/security', label: 'Security', icon: <Shield />, any: ['security.view', 'security.report', 'security.gate', 'security.patrol', 'security.keys'] },
      { to: '/management', label: 'Management', icon: <Briefcase />, any: ['mgmt.manage', 'mgmt.view', 'mgmt.directives'] },
      { to: '/it', label: 'IT', icon: <Laptop />, perm: 'it.view' },
      { to: '/general-services', label: 'General Services', icon: <Bus />, any: ['gs.view', 'transport.book', 'transport.dispatch', 'transport.drive', 'grounds.work', 'prayer.manage'] },
      { to: '/work-orders', label: 'Work Orders', icon: <Wrench />, perm: 'engineering.view' },
    ],
  },
  {
    section: 'Food & Beverage',
    items: [
      { to: '/fnb', label: 'F&B overview', icon: <Soup />, perm: 'fnb.view' },
      { to: '/pos', label: 'Outlet POS', icon: <UtensilsCrossed />, perm: 'pos.use' },
      { to: '/breakfast', label: 'Breakfast', icon: <Coffee />, perm: 'fnb.breakfast' },
      { to: '/bookings', label: 'Table bookings', icon: <CalendarClock />, perm: 'fnb.bookings' },
      { to: '/rooftop', label: 'Rooftop', icon: <Sun />, perm: 'leisure.view' },
      // The bartender on duty runs the pool from the rooftop till (owner, 2026-10-27).
      { to: '/rooftop', label: 'Pool', icon: <Waves />, perm: 'leisure.pool', hide: 'leisure.view' },
      { to: '/kitchen-office', label: 'Kitchen office', icon: <CookingPot />, any: ['kitchen.view', 'kitchen.cost'] },
      { to: '/kitchen', label: 'Kitchen Display', icon: <ChefHat />, perm: 'pos.kitchen' },
      // Every till's sales, the pool's, the gym's and the laundry's too: the GM's (F&B reads F&B -> Figures).
      { to: '/outlet-sales', label: 'Outlet Sales', icon: <BarChart3 />, perm: 'pos.comp' },
    ],
  },
  {
    section: 'Stores & Assets',
    items: [
      { to: '/stores', label: 'Stores & Stock', icon: <Warehouse />, any: ['inventory.view', 'inventory.request', 'inventory.request_approve', 'inventory.waste', 'inventory.issue', 'inventory.count', 'inventory.purchase', 'inventory.approve', 'pettycash.record'] },
      { to: '/assets', label: 'Assets & Energy', icon: <Cog />, any: ['assets.view', 'energy.record', 'energy.view'] },
    ],
  },
  {
    section: 'Sales & Events',
    items: [
      { to: '/sales', label: 'Sales & Marketing', icon: <Handshake />, perm: 'sales.view' },
      { to: '/events', label: 'Events & Meetings', icon: <PartyPopper />, perm: 'events.view' },
      // Whoever takes money takes an event's deposit and payment here, without the Events page (owner, 2026-10-31).
      { to: '/event-bills', label: 'Event bills', icon: <Receipt />, any: ['folios.post_payment', 'folios.view'] },
      { to: '/groups', label: 'Group Blocks', icon: <UsersRound />, perm: 'reservations.view' },
    ],
  },
  {
    section: 'Revenue & Distribution',
    items: [
      { to: '/rates', label: 'Rates & Restrictions', icon: <Tags />, perm: 'rates.view' },
      { to: '/channel', label: 'Channel Manager', icon: <Network />, perm: 'channel.manage' },
      { to: '/book', label: 'Booking Engine', icon: <Globe />, perm: 'booking_engine.manage' },
    ],
  },
  {
    section: 'Profiles',
    items: [
      { to: '/guests', label: 'Guests', icon: <Users />, perm: 'guests.view' },
      { to: '/companies', label: 'Companies & Agents', icon: <Building2 />, perm: 'companies.view' },
    ],
  },
  {
    section: 'Finance',
    items: [
      // The General Cashier's desk: where he starts (owner, 2026-10-30).
      { to: '/cashier', label: "Cashier's desk", icon: <ListTodo />, perm: 'shift.slips' },
      { to: '/accounts', label: 'Accounts', icon: <Calculator />, any: ['gl.view', 'pettycash.record', 'pettycash.approve'] },
      { to: '/finance/transfers', label: 'Money in', icon: <ClipboardCheck />, any: ['payments.verify_transfers', 'bank.settlements', 'bank.reconcile', 'reports.financial', 'folios.let_go_owing', 'reports.moneywatch'] },
      { to: '/finance/cashier-day', label: 'Daily reconciliation', icon: <ListChecks />, any: ['shift.view', 'bank.reconcile', 'bank.view'] },
      { to: '/finance/card-disputes', label: 'Card disputes', icon: <ShieldAlert />, any: ['card.disputes', 'bank.reconcile', 'bank.view'] },
      { to: '/night-audit', label: 'Night Audit', icon: <MoonStar />, perm: 'nightaudit.run' },
      { to: '/night-audit-reports', label: 'Night audit reports', icon: <FileText />, perm: 'nightaudit.report.view' },
      { to: '/finance/income-audit', label: 'Income audit', icon: <ClipboardCheck />, any: ['audit.signoff', 'reports.financial'] },
      { to: '/finance/money-watch', label: 'Money Watch', icon: <Eye />, perm: 'reports.moneywatch' },
      { to: '/finance/packs', label: "Owner's monthly pack", icon: <FileBarChart />, any: ['reports.financial', 'reports.moneywatch', 'pack.prepare', 'pack.review'] },
      { to: '/finance/statements', label: 'Financial Statements', icon: <FileBarChart />, perm: 'reports.financial' },
      { to: '/finance/ledger', label: 'General Ledger', icon: <BookText />, perm: 'gl.view' },
      { to: '/finance/receivables', label: 'Receivables (AR)', icon: <HandCoins />, perm: 'ar.view' },
      { to: '/finance/payables', label: 'Payables (AP)', icon: <Truck />, perm: 'ap.view' },
      { to: '/finance/confirm-services', label: 'Supplier work to confirm', icon: <ClipboardCheck />, perm: 'ap.confirm_service' },
      { to: '/finance/bank', label: 'Bank & Settlements', icon: <Landmark />, any: ['bank.reconcile', 'bank.settlements', 'bank.view'] },
      { to: '/fx', label: 'Exchange Rates', icon: <Coins />, any: ['rates.view', 'fx.manage'] },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/admin/users', label: 'Users & Roles', icon: <UserCog />, perm: 'admin.users' },
      { to: '/hr', label: 'Human Resources', icon: <IdCard />, any: ['hr.view', 'hr.team', 'hr.manage', 'leave.approve'] },
      { to: '/payroll', label: 'Payroll', icon: <Wallet />, perm: 'payroll.view' },
      { to: '/attendance', label: 'Rosters & Attendance', icon: <CalendarClock />, perm: 'attendance.manage' },
      { to: '/admin/audit', label: 'Audit Trail', icon: <ShieldCheck />, perm: 'admin.audit_log' },
      { to: '/admin/privacy', label: 'Data Protection', icon: <FileLock2 />, perm: 'privacy.manage' },
      { to: '/admin/integrations', label: 'Integrations', icon: <Plug />, perm: 'admin.integrations' },
    ],
  },
];

/** A menu entry the user's permissions allow. */
function visible(i: NavItem, can: (p: Permission) => boolean): boolean {
  return (!i.perm || can(i.perm)) && (!i.any || i.any.some((p) => can(p))) && (!i.hide || !can(i.hide));
}

/** Idle minutes after which a group tablet signs out, so the next person never works under someone else's name. */
const TABLET_IDLE_MINUTES = 10;

/** On a registered group tablet: sign out after a quiet spell. */
function useTabletIdleLock(enabled: boolean, onIdle: () => void) {
  // The latest callback, without restarting the idle clock on every render.
  const idle = useRef(onIdle);
  idle.current = onIdle;
  useEffect(() => {
    if (!enabled) return;
    let last = Date.now();
    const touch = () => {
      last = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });
    const timer = setInterval(() => {
      if (Date.now() - last > TABLET_IDLE_MINUTES * 60_000) idle.current();
    }, 15_000);
    return () => {
      for (const e of events) window.removeEventListener(e, touch);
      clearInterval(timer);
    };
  }, [enabled]);
}

export function AppShell() {
  const { user, can, logout } = useAuth();
  const tablet = getDevice();
  useTabletIdleLock(!!tablet, () => void logout());
  const { data: ref } = useLookups();
  // Front-desk computers keep the emergency list up to date in the background.
  useEmergencySync(can('frontdesk.emergency_list'));
  // Receptionists hear when housekeeping releases a room a guest is waiting for.
  useRoomReadyAlerts(can('frontdesk.checkin'));
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const sidebar = (
    <nav className="flex h-full flex-col bg-brand-950 text-brand-100">
      <div className="flex items-center gap-2.5 px-4 py-4">
        <div className="grid size-8 place-items-center rounded-lg bg-brand-800 text-sm font-bold text-gold-400 ring-1 ring-gold-500/40">H</div>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-white">{ref?.property.tradingName ?? 'Hotel PMS'}</div>
          <div className="text-[11px] text-brand-300">Property Management</div>
        </div>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-2 pb-4">
        {NAV.map((s) => {
          const items = s.items.filter((i) => visible(i, can));
          if (!items.length) return null;
          return (
            <div key={s.section}>
              <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-400/80">{s.section}</div>
              {items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cls(
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] transition-colors [&>svg]:size-4 [&>svg]:shrink-0',
                      isActive ? 'bg-brand-800 font-medium text-white shadow-inner' : 'text-brand-200 hover:bg-brand-900 hover:text-white',
                    )
                  }
                >
                  {i.icon}
                  {i.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>
      <div className="border-t border-brand-900 px-4 py-3 text-[11px] text-brand-400">v0.1 · {ref?.property.code}</div>
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-60 lg:block">{sidebar}</aside>
      {open && (
        <div className="no-print fixed inset-0 z-40 lg:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-slate-950/50" />
          <div className="absolute inset-y-0 left-0 w-64" onClick={(e) => e.stopPropagation()}>
            {sidebar}
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-60 print:pl-0">
        <header className="no-print sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur">
          <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} aria-label="Menu">
            <Menu className="size-5" />
          </button>
          {/* Reservation search only for those who see reservations (not HR, the kitchen or the laundry, say). */}
          {can('reservations.view') && (
            <form
              className="relative max-w-md flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                if (q.trim()) navigate(`/reservations?q=${encodeURIComponent(q.trim())}`);
              }}
            >
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Find reservation — name, confirmation no., room, company…"
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-3 text-[13px] placeholder:text-slate-400 focus:border-brand-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-600/20"
              />
            </form>
          )}
          <div className="ml-auto flex items-center gap-3">
            {tablet && (
              <button
                type="button"
                onClick={() => void logout()}
                title={`${tablet.name}: hand the tablet to the next person`}
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-700 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-800"
              >
                <Repeat2 className="size-3.5" /> Switch person
              </button>
            )}
            <ShiftButton />
            {can('attendance.clock') && <ClockButton />}
            {/* The hotel's trading day: for those who book, sell, keep the rooms or the books; not HR, IT, security or maintenance. */}
            {ref && TRADING.some((p) => can(p)) && (
              <div className="hidden items-center gap-2 rounded-lg border border-gold-500/40 bg-yellow-50 px-2.5 py-1 sm:flex" title="Hotel business date">
                <CalendarDays className="size-3.5 text-gold-600" />
                <span className="text-[11px] font-medium uppercase tracking-wide text-yellow-800">Business date</span>
                <span className="num text-[13px] font-semibold text-slate-900">{date(ref.businessDate, 'long')}</span>
              </div>
            )}
            <Dropdown.Root>
              <Dropdown.Trigger className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-100">
                <span className="grid size-7 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
                  {user?.fullName.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-[13px] font-medium leading-4">{user?.fullName}</span>
                  <span className="block text-[11px] leading-4 text-slate-500">{user?.roles.join(', ')}</span>
                </span>
                <ChevronDown className="size-3.5 text-slate-400" />
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content align="end" sideOffset={6} className="z-50 min-w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                  <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] outline-none data-[highlighted]:bg-slate-100" onSelect={() => navigate('/account/security')}>
                    <ShieldCheck className="size-4 text-slate-400" /> Two-step sign-in
                  </Dropdown.Item>
                  <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] outline-none data-[highlighted]:bg-slate-100" onSelect={() => navigate('/account/password')}>
                    <KeyRound className="size-4 text-slate-400" /> Change password
                  </Dropdown.Item>
                  <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] outline-none data-[highlighted]:bg-slate-100" onSelect={() => navigate('/my-notices')}>
                    <Megaphone className="size-4 text-slate-400" /> Notices &amp; my actions
                  </Dropdown.Item>
                  <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] outline-none data-[highlighted]:bg-slate-100" onSelect={() => navigate('/it-problems')}>
                    <Wrench className="size-4 text-slate-400" /> Report an IT problem
                  </Dropdown.Item>
                  <Dropdown.Item className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] text-rose-700 outline-none data-[highlighted]:bg-rose-50" onSelect={() => void logout()}>
                    <LogOut className="size-4" /> Sign out
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-6 sm:px-6 print:max-w-none print:p-0">
          <PrintStamp hotel={ref?.property.tradingName ?? 'Hotel'} by={user?.fullName ?? ''} />
          <div className="no-print">
            <AuditOverdueBanner />
            <NoticeBanner />
          </div>
          <Suspense fallback={<Spinner label="Loading…" />}>
            <ShiftGate>
              <Outlet />
            </ShiftGate>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/** On paper only: the hotel, when it was printed and by whom (owner, 2026-10-29). */
function PrintStamp({ hotel, by }: { hotel: string; by: string }) {
  const stamp = () => new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short' }).format(new Date());
  const [at, setAt] = useState(stamp);
  useEffect(() => {
    const before = () => setAt(stamp());
    window.addEventListener('beforeprint', before);
    return () => window.removeEventListener('beforeprint', before);
  }, []);
  return <div className="mb-3 hidden border-b border-slate-300 pb-1 text-[11px] text-slate-600 print:block">{hotel} · printed {at}{by ? ` by ${by}` : ''}</div>;
}

/** Where staff without the dashboard start their shift: the screen they work from all day. */
const WORKSTATIONS: { to: string; perm: Permission }[] = [
  { to: '/my-rooms', perm: 'housekeeping.update_status' }, { to: '/pos', perm: 'pos.use' }, { to: '/cashier', perm: 'shift.slips' },
  // Purchasing on the requests waiting for it; the Storekeeper on the requisitions to issue (owner, 2026-11-01).
  { to: '/stores?tab=requests', perm: 'inventory.purchase' }, { to: '/stores?tab=requisitions', perm: 'inventory.issue' },
];
/** A department's own dashboard, where its people start after signing in: HR on the HR overview, the laundry on its board. */
/** `unless`: not for someone who also holds this (the administrator, in IT, keeps the hotel dashboard). */
const DEPARTMENT_HOME: Record<string, { to: string; perm: Permission | Permission[]; unless?: Permission }> = {
  HR: { to: '/hr?tab=overview', perm: 'hr.view' },
  LDY: { to: '/laundry', perm: 'laundry.view' },
  SAL: { to: '/sales', perm: 'sales.view' },
  FO: { to: '/front-desk', perm: 'frontdesk.checkin' },
  FNB: { to: '/fnb', perm: 'fnb.view' },
  KIT: { to: '/kitchen-office', perm: 'kitchen.view' },
  FIN: { to: '/accounts', perm: 'gl.view' },
  ENG: { to: '/maintenance', perm: ['maintenance.view', 'engineering.work'] },
  // The head of housekeeping opens on the board (the assistant and the room attendants on the tablet page).
  HK: { to: '/housekeeping', perm: 'housekeeping.team' },
  SEC: { to: '/security', perm: 'security.gate' },
  EXEC: { to: '/management', perm: ['mgmt.manage', 'mgmt.view', 'mgmt.directives'] },
  IT: { to: '/it', perm: 'it.view', unless: 'admin.users' },
  GS: { to: '/general-services', perm: ['gs.view', 'transport.drive', 'grounds.work', 'prayer.manage'] },
};

/**
 * Landing page: the department's own dashboard right after signing in (HR), the hotel dashboard,
 * the user's workstation (e.g. the housekeeping tablet), or the first area the role can open.
 */
export function Home({ dashboard }: { dashboard: ReactNode }) {
  const { can, user } = useAuth();
  const location = useLocation();
  const own = user?.departmentCode ? DEPARTMENT_HOME[user.departmentCode] : undefined;
  const signedIn = !!(location.state as { signedIn?: boolean } | null)?.signedIn;
  if (own && [own.perm].flat().some((p) => can(p)) && !(own.unless && can(own.unless)) && (signedIn || !can('dashboard.view'))) return <Navigate to={own.to} replace />;
  if (can('dashboard.view')) return <>{dashboard}</>;
  const station = WORKSTATIONS.find((w) => can(w.perm));
  if (station) return <Navigate to={station.to} replace />;
  const first = NAV.flatMap((s) => s.items).find((i) => visible(i, can));
  return first ? <Navigate to={first.to} replace /> : <Forbidden />;
}

/** New notices and policies to confirm, shown on every page until read (the GM's notices to staff). */
/** Roles that work by the hotel's trading day (the same as see the business-date badge). */
// The general cashiers work by the hotel day too (owner, 2026-10-30).
const TRADING: Permission[] = ['reservations.view', 'pos.use', 'fnb.view', 'housekeeping.view', 'gl.view', 'nightaudit.run', 'bank.settlements'];

/**
 * The hotel date moves only when the night audit closes the day. From the audit time (02:00) on the next calendar day,
 * everyone who works by the hotel date sees that it has not been run, and the night auditor gets the button to run it.
 */
function AuditOverdueBanner() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: ref } = useLookups();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // The clock moves on, and another computer may run the audit: look again every few minutes.
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    const reload = setInterval(() => void qc.invalidateQueries({ queryKey: ['lookups'] }), 5 * 60_000);
    return () => (clearInterval(tick), clearInterval(reload));
  }, [qc]);
  if (!ref || !TRADING.some((p) => can(p))) return null;
  const local = new Date(now + 3_600_000); // the hotel clock (West Africa Time, UTC+1)
  const today = local.toISOString().slice(0, 10);
  const minute = local.getUTCHours() * 60 + local.getUTCMinutes();
  const [h, m] = (ref.property.settings?.operations?.night_audit_time ?? '02:00').split(':').map(Number) as [number, number];
  const nextDay = new Date(Date.parse(`${ref.businessDate}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
  if (!(today > nextDay || (today === nextDay && minute >= h * 60 + m))) return null;
  const behind = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${ref.businessDate}T12:00:00Z`)) / 86_400_000);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-900">
      <TriangleAlert className="size-4 shrink-0 text-rose-600" />
      <span className="flex-1">
        The night audit for <b>{date(ref.businessDate, 'long')}</b> has not been run, so the hotel is still on that date
        {behind > 1 ? ` (${behind} days behind the calendar)` : ''}. Today's arrivals, departures and charges wait until it is run.
      </span>
      {can('nightaudit.run') ? (
        <Button size="sm" variant="danger" icon={<MoonStar className="size-3.5" />} onClick={() => navigate('/night-audit')}>
          Run the night audit
        </Button>
      ) : (
        <span className="text-rose-800">Tell the night auditor or the Front Office Manager.</span>
      )}
    </div>
  );
}

function NoticeBanner() {
  const location = useLocation();
  const { data } = useQuery({ queryKey: ['mgmt', 'my-notices'], queryFn: () => get<{ unread: number; toConfirm: number }>('/management/notices/mine'), refetchInterval: 5 * 60_000, staleTime: 60_000 });
  if (!data || (!data.unread && !data.toConfirm) || location.pathname === '/my-notices') return null;
  return (
    <NavLink to="/my-notices" className="mb-4 flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-900 hover:bg-brand-100">
      <Megaphone className="size-4" /> {data.unread ? `${data.unread} new notice${data.unread > 1 ? 's' : ''} from management` : ''}{data.unread && data.toConfirm ? ' · ' : ''}{data.toConfirm ? `${data.toConfirm} to confirm` : ''} — read now
    </NavLink>
  );
}

export function Forbidden() {
  return (
    <div className="card mx-auto mt-10 max-w-md p-8 text-center">
      <Receipt className="mx-auto mb-3 size-8 text-slate-300" />
      <h1 className="font-semibold">Not available for your role</h1>
      <p className="mt-1 text-sm text-slate-500">Ask a manager if you need access to this area.</p>
    </div>
  );
}
